import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import AdminShell, { Body, Section, Stat } from "@/components/admin/AdminShell";
import { Card, Spinner } from "@/components/Common";
import { api, enc, errMsg } from "@/lib/api";
import { SCORE_NOTE, label, num, pct, useAdminGet, usd, when } from "@/lib/admin";
import { cn } from "@/lib/utils";

const Bars = ({ rows, k = "count", name = "range", extra }) => {
  const max = Math.max(1, ...rows.map((r) => r[k] || 0));
  return (
    <Card className="space-y-2">
      {rows.map((r) => (
        <div key={r[name]} className="grid grid-cols-[110px_1fr_auto] items-center gap-3 text-xs">
          <span className="truncate text-zinc-300">{r[name]}</span>
          <div className="h-2 overflow-hidden rounded-full bg-[#27272A]"><div className="h-full rounded-full bg-[#D4AF37]" style={{ width: `${(100 * (r[k] || 0)) / max}%` }} /></div>
          <span className="font-mono text-zinc-300">{typeof r[k] === "number" && k === "revenue" ? usd(r[k]) : num(r[k])}{extra && <span className="ml-2 text-zinc-500">{extra(r)}</span>}</span>
        </div>
      ))}
    </Card>
  );
};

const objRows = (o) => (Array.isArray(o) ? o : Object.entries(o || {}).map(([name, count]) => ({ name, count: typeof count === "object" ? count.count ?? count.value : count })));

export function ResumeIntel() {
  const q = useAdminGet("/admin/cc/resume-intel");
  return (
    <AdminShell title="Resume Intelligence" subtitle="How uploaded resumes score and convert.">
      <Body q={q}>{(d) => (
        <>
          <div className="mb-3 grid grid-cols-2 gap-3 lg:grid-cols-5">
            <Stat k="Uploads" v={num(d.total_uploads)} /><Stat k="Assessed" v={num(d.assessments_completed)} /><Stat k="Pending" v={num(d.pending)} />
            <Stat k="Failed" v={num(d.failed)} tone={d.failed ? "text-red-300" : ""} /><Stat k="Average score" v={d.average_score ?? "—"} tone="text-[#F3C649]" testid="cc-ri-average" />
          </div>
          <p className="mb-8 text-xs text-zinc-500" data-testid="cc-ri-score-note">{d.score_note || SCORE_NOTE}{d.completion_time ? ` · Typical completion: ${typeof d.completion_time === "object" ? Object.entries(d.completion_time).map(([k, v]) => `${label(k)} ${v}`).join(", ") : d.completion_time}` : ""}</p>
          <div className="grid gap-6 lg:grid-cols-2">
            {d.distribution?.length > 0 && <Section title="Score distribution"><Bars rows={d.distribution} extra={(r) => `${pct(r.conversion_rate)} convert`} /></Section>}
            {d.common_opportunities?.length > 0 && <Section title="Most common opportunities"><Bars rows={d.common_opportunities} name="label" /></Section>}
          </div>
        </>
      )}</Body>
    </AdminShell>
  );
}

export function Revenue() {
  const q = useAdminGet("/admin/cc/attribution");
  const r = useAdminGet("/admin/cc/referrals");
  return (
    <AdminShell title="Revenue & Referrals" subtitle="Where revenue comes from. Actual money only.">
      <Body q={q}>{(d) => (
        <>
          <div className="mb-8 grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Stat k="Pricing page conversion" v={pct(d.pricing_conversion?.rate ?? d.pricing_conversion)} />
            <Stat k="Checkout abandonment" v={pct(d.checkout_abandonment?.rate ?? d.checkout_abandonment)} />
            <Stat k="Assisted conversions" v={num(typeof d.assisted_conversions === "object" ? d.assisted_conversions?.count : d.assisted_conversions)} />
            <Stat k="LTV" v={usd(d.ltv)} sub={d.ltv_note} />
            <Stat k="CAC" v={d.cac == null ? "Not tracked" : usd(d.cac)} sub={d.cac_note} testid="cc-cac" />
          </div>
          <div className="grid gap-6 lg:grid-cols-2">
            {d.revenue_by_plan && <Section title="Revenue by plan"><Bars rows={objRows(d.revenue_by_plan).map((x) => ({ name: x.name, revenue: x.revenue ?? x.count }))} name="name" k="revenue" /></Section>}
            {d.leads_by_source && <Section title="Leads by source"><Bars rows={objRows(d.leads_by_source)} name="name" /></Section>}
            {d.first_touch?.length > 0 && <Section title="First-touch revenue"><Bars rows={d.first_touch} name="name" k="revenue" /></Section>}
            {d.last_touch?.length > 0 && <Section title="Last-touch revenue"><Bars rows={d.last_touch} name="name" k="revenue" /></Section>}
            {d.time_to_conversion && <Section title="Time to conversion"><Bars rows={objRows(d.time_to_conversion)} name="name" /></Section>}
          </div>
        </>
      )}</Body>
      <Section title="Referrals" testid="cc-referrals">
        <Body q={r}>{(x) => (
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Stat k="Codes" v={num(typeof x.codes === "object" ? x.codes?.length ?? x.codes?.count : x.codes)} /><Stat k="Registrations" v={num(x.registrations)} />
            <Stat k="Conversions" v={num(x.conversions)} /><Stat k="Revenue" v={usd(x.revenue)} tone="text-emerald-300" />
          </div>
        )}</Body>
      </Section>
    </AdminShell>
  );
}

const riskCls = (v) => (v >= 70 ? "text-red-400 border-red-500/40" : v >= 40 ? "text-orange-300 border-orange-500/40" : "text-emerald-300 border-emerald-500/40");

export function Health() {
  const q = useAdminGet("/admin/cc/health");
  return (
    <AdminShell title="Customer Health" subtitle="Paying customers, highest cancellation risk first.">
      <Body q={q}>{(d) => (
        <div className="space-y-2" data-testid="cc-health-list">
          {!(d.items || []).length && <p className="text-sm text-zinc-500">No customers yet.</p>}
          {(d.items || []).map((c) => (
            <Link key={c.key} to={`/admin/cc/prospects/${enc(c.key)}`} className="flex flex-col gap-3 rounded-xl border border-[#27272A] bg-[#09090B] p-4 hover:border-[#D4AF37]/40 md:flex-row md:items-center" data-testid={`cc-health-${c.key}`}>
              <span className={cn("flex h-12 w-12 shrink-0 items-center justify-center rounded-full border-2 font-mono text-sm font-bold", riskCls(c.cancellation_risk))}>{c.cancellation_risk}</span>
              <div className="min-w-0 flex-1"><div className="truncate text-sm text-zinc-100">{c.name || c.email} <span className="text-xs text-zinc-500">· {c.tier}</span></div>
                {c.risk_reasons?.length > 0 && <div className="mt-1 flex flex-wrap gap-1">{c.risk_reasons.map((r) => <span key={r} className="rounded bg-[#18181B] px-1.5 py-0.5 text-[10px] text-zinc-400">{r}</span>)}</div>}</div>
              <div className="text-xs text-zinc-500 md:text-right">Last login {when(c.last_login)}<br />{num(c.applications)} applications · {num(c.feature_uses_30d)} feature uses (30d)<br />Renews {when(c.renewal)}</div>
            </Link>
          ))}
        </div>
      )}</Body>
    </AdminShell>
  );
}

export function AICampaigns() {
  const [prompt, setPrompt] = useState("");
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async () => { setBusy(true); try { setRes((await api.post("/admin/cc/ai-campaign", { prompt })).data); toast.success("Draft created"); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); } };
  const ai = res?.campaign?.ai || {};
  return (
    <AdminShell title="AI Content Studio" subtitle="Describe a campaign. The AI drafts the email and audience. Nothing sends until you approve it in Email Marketing.">
      <Card className="max-w-3xl space-y-3">
        <Textarea rows={4} value={prompt} onChange={(e) => setPrompt(e.target.value)} placeholder="e.g. Win back free users who scanned a resume in the last 30 days but never opened pricing" data-testid="cc-ai-prompt" />
        <Button onClick={run} disabled={busy || prompt.trim().length < 10} data-testid="cc-ai-generate">{busy ? <><Spinner />Drafting your campaign…</> : <><Sparkles className="h-4 w-4" />Generate draft campaign</>}</Button>
      </Card>
      {res && (
        <Card className="mt-6 max-w-3xl space-y-4 border-[#D4AF37]/40" data-testid="cc-ai-result">
          <div className="flex flex-wrap items-center justify-between gap-2"><h3 className="font-display text-xl text-zinc-100">{res.campaign?.name}</h3><span className="rounded-full border border-[#27272A] px-2 py-0.5 text-[10px] uppercase text-zinc-400">Draft · {num(res.audience_count)} people</span></div>
          <div className="grid gap-3 text-sm sm:grid-cols-2">
            {[["Objective", ai.objective], ["Timing", ai.timing], ["Call to action", ai.cta], ["Success metrics", Array.isArray(ai.success_metrics) ? ai.success_metrics.join(", ") : ai.success_metrics]].map(([k, v]) => v && <div key={k}><div className="text-[11px] uppercase text-zinc-500">{k}</div><div className="text-zinc-200">{v}</div></div>)}
          </div>
          {res.template && <div className="rounded-lg border border-[#27272A] bg-[#09090B] p-3"><div className="text-sm font-medium text-zinc-100">{res.template.subject}</div><iframe title="Draft email" sandbox="" srcDoc={res.template.body} className="mt-2 h-60 w-full rounded bg-white" data-testid="cc-ai-template-frame" /></div>}
          <Link to="/admin/mk?tab=campaigns"><Button data-testid="cc-ai-review">Review and approve in Email Marketing</Button></Link>
        </Card>
      )}
    </AdminShell>
  );
}
